import React, { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';

import useAnimate from './primitives/Motion';

import Reveal from './primitives/Reveal';

const EASE = [0.16, 1, 0.3, 1];

/**
 * One card in the deck. The slot stays in normal flow so its scroll progress is
 * measurable; the inner shell is what actually sticks. As the next card climbs
 * over this one, the card underneath shrinks and dims: the deck effect.
 */
export default function CompanyCard({ company, index, count, stacked }) {
    const slotRef = useRef(null);
    const reduced = !useAnimate();

    // The last card has no card climbing over it, so it never recedes.
    const isLast = index === count - 1;
    const animate = stacked && !reduced && !isLast;

    const { scrollYProgress } = useScroll({
        target: slotRef,
        offset: ['start start', 'end start']
    });

    // A slot spans every stride still to come, so the card directly above this
    // one finishes covering it after the first of those strides.
    const coverEnd = 1 / Math.max(1, count - 1 - index);

    const scale = useTransform(scrollYProgress, [0, coverEnd], [1, 0.9]);
    const dim = useTransform(scrollYProgress, [0, coverEnd], [0, 0.55]);

    const { scrollYProgress: passProgress } = useScroll({
        target: slotRef,
        offset: ['start end', 'end start']
    });

    const photoY = useTransform(passProgress, [0, 1], ['-9%', '9%']);

    return (
        <div
            className="deck__slot"
            ref={slotRef}
            style={{
                '--accent': company.accent,
                '--i': String(index),
                '--focus': company.focus
            }}
        >
            <div className="deck__sticky">
                <motion.article
                    className="company"
                    id={company.id}
                    style={animate ? { scale } : undefined}
                >
                    <div className="company__media">
                        <motion.img
                            className="company__photo"
                            src={company.photo}
                            alt=""
                            loading="lazy"
                            decoding="async"
                            style={reduced ? undefined : { y: photoY }}
                        />
                        <span className="company__media-veil" aria-hidden="true" />
                        <span className="company__index">{String(index + 1).padStart(2, '0')}</span>

                        <Reveal className="company__lockup" delay={0.08} y={20} amount={0.2}>
                            <img
                                className="company__logo"
                                src={company.logo}
                                alt=""
                                width="1920"
                                height="1080"
                                loading="lazy"
                                decoding="async"
                            />
                        </Reveal>
                    </div>

                    <div className="company__body">
                        <Reveal delay={0.05} y={20} amount={0.2}>
                            <span className="company__discipline">{company.discipline}</span>
                        </Reveal>

                        <Reveal delay={0.1} y={20} amount={0.2}>
                            <h3 className="company__name">{company.name}</h3>
                        </Reveal>

                        <Reveal delay={0.16} y={20} amount={0.2}>
                            <p className="company__copy">{company.body}</p>
                        </Reveal>

                        <hr className="rule company__rule" />

                        <div className="company__services">
                            <span className="company__services-label">Services</span>
                            <ul>
                                {company.services.map((service, i) => (
                                    <motion.li
                                        key={service}
                                        initial={reduced ? false : { opacity: 0, y: 10 }}
                                        whileInView={{ opacity: 1, y: 0 }}
                                        viewport={{ once: true, amount: 0.4 }}
                                        transition={{ duration: 0.5, delay: 0.02 * i, ease: EASE }}
                                    >
                                        {service}
                                    </motion.li>
                                ))}
                            </ul>
                        </div>

                        <div className="company__actions">
                            <a
                                className="btn btn--accent"
                                href={company.url}
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                Visit {company.short}
                                <span className="btn__arrow" aria-hidden="true">→</span>
                            </a>
                            <a
                                className="company__social"
                                href={company.instagram}
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                Instagram
                            </a>
                        </div>
                    </div>

                    {animate && (
                        <motion.span
                            className="company__dim"
                            style={{ opacity: dim }}
                            aria-hidden="true"
                        />
                    )}
                </motion.article>
            </div>
        </div>
    );
}
